import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Phone, Mail, MapPin, Calendar, Droplet, Activity, Award, Edit, MessageCircle, Clock, CheckCircle, XCircle, FileText, AlertTriangle, ArrowLeft } from 'lucide-react';

const DonorDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [donor, setDonor] = useState(null);
  const [donationHistory, setDonationHistory] = useState([]);

  useEffect(() => {
    fetchDonorDetails();
  }, [id]);

  const fetchDonorDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3011'}/admin/donors/${id}`,
        {
          headers: {
            'x-auth-token': token
          }
        }
      );
      
      console.log('Donor details received:', response.data);
      setDonor(response.data);
      
      // Fetch donation history if available
      if (response.data.user && response.data.user._id) {
        try {
          const historyResponse = await axios.get(
            `${process.env.REACT_APP_API_URL || 'http://localhost:3011'}/admin/donors/${response.data.user._id}/donations`,
            {
              headers: {
                'x-auth-token': token
              }
            }
          );
          console.log('Donation history received:', historyResponse.data);
          setDonationHistory(historyResponse.data || []);
        } catch (err) {
          console.error('Error fetching donation history:', err);
          // Don't set error state here, just log it
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching donor details:', err);
      setError('Failed to fetch donor details. Please try again.');
      setLoading(false);
    }
  };

  const handleEditDonor = () => {
    navigate(`/admin/dashboard/donors/form?id=${id}`);
  };

  const handleContactDonor = (method) => {
    if (!donor || !donor.user) return;
    
    if (method === 'email' && donor.user.email) {
      window.location.href = `mailto:${donor.user.email}`;
    } else if (method === 'phone' && donor.user.phone) {
      window.location.href = `tel:${donor.user.phone}`;
    }
  };

  // Format date to readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate time since last donation
  const getTimeSinceLastDonation = (dateString) => {
    if (!dateString) return 'Never donated';
    
    const lastDonation = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - lastDonation);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) {
      return `${diffDays} days ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
      const years = Math.floor(diffDays / 365);
      return `${years} year${years > 1 ? 's' : ''} ago`;
    }
  };

  // Calculate time since registration
  const getTimeSinceRegistration = (dateString) => {
    if (!dateString) return 'Unknown';
    
    const registrationDate = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - registrationDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) {
      return `${diffDays} days ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
      const years = Math.floor(diffDays / 365);
      return `${years} year${years > 1 ? 's' : ''} ago`;
    }
  };

  // Get eligibility status
  const getEligibilityStatus = (lastDonationDate) => {
    if (!lastDonationDate) return { status: 'Eligible', color: 'text-green-600' };
    
    const lastDonation = new Date(lastDonationDate);
    const now = new Date();
    const diffTime = Math.abs(now - lastDonation);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Most blood donation centers require 56 days (8 weeks) between whole blood donations
    if (diffDays < 56) {
      const daysLeft = 56 - diffDays;
      return { 
        status: `Eligible in ${daysLeft} days`, 
        color: 'text-yellow-600' 
      };
    }
    
    return { status: 'Eligible', color: 'text-green-600' };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  if (!donor || !donor.user) {
    return (
      <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">No data!</strong>
        <span className="block sm:inline"> No donor information found.</span>
      </div>
    );
  }

  const { user, profile } = donor;
  const eligibility = getEligibilityStatus(profile?.lastDonationDate);
  const bloodGroup = user.bloodGroup || (profile && profile.bloodType) || 'Unknown';

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Donor Details</h2>
        <button 
          onClick={() => navigate('/admin/dashboard/donors')}
          className="flex items-center px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Donors
        </button>
      </div>
      
      {/* Donor ID and Status */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <span className="text-sm text-gray-500">Donor ID:</span>
            <span className="ml-2 font-mono">{user._id}</span>
          </div>
          <div className="flex items-center">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${profile?.isAvailable ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
              {profile?.isAvailable ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
          {/* Personal Information */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Personal Information</h3>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <User className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Full Name</h4>
                  <p className="text-base font-medium text-gray-900">{user.name || 'N/A'}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Calendar className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Age</h4>
                  <p className="text-base font-medium text-gray-900">{user.age ? `${user.age} years` : 'N/A'}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <User className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Gender</h4>
                  <p className="text-base font-medium text-gray-900">{user.gender || profile?.gender || 'N/A'}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Droplet className="h-5 w-5 text-red-500 mr-3 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Blood Type</h4>
                  <p className="text-base font-medium text-gray-900">{bloodGroup}</p>
                </div>
              </div>

              <div className="flex items-start">
                <FileText className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Registration Date</h4>
                  <p className="text-base font-medium text-gray-900">{user.createdAt ? formatDate(user.createdAt) : 'N/A'}</p>
                  <p className="text-xs text-gray-500">{user.createdAt ? getTimeSinceRegistration(user.createdAt) : ''}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Contact Information */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Contact Information</h3>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <Mail className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-500">Email</h4>
                  <div className="flex justify-between items-center">
                    <p className="text-base font-medium text-gray-900">{user.email || 'N/A'}</p>
                    {user.email && (
                      <button 
                        onClick={() => handleContactDonor('email')}
                        className="text-blue-600 hover:text-blue-800"
                        title="Send Email"
                      >
                        <MessageCircle className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-start">
                <Phone className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-500">Phone</h4>
                  <div className="flex justify-between items-center">
                    <p className="text-base font-medium text-gray-900">{user.phone || profile?.phone || 'N/A'}</p>
                    {(user.phone || profile?.phone) && (
                      <button 
                        onClick={() => handleContactDonor('phone')}
                        className="text-green-600 hover:text-green-800"
                        title="Call"
                      >
                        <Phone className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-start">
                <MapPin className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Address</h4>
                  <p className="text-base font-medium text-gray-900">
                    {user.address || 
                     (profile && profile.address && typeof profile.address === 'object' ? 
                      profile.address.street : 
                      (profile && profile.address ? profile.address : 'N/A'))}
                  </p>
                  {(user.city || user.state || (profile && profile.address && 
                    (typeof profile.address === 'object' ? 
                      (profile.address.city || profile.address.state) : false))) && (
                    <p className="text-sm text-gray-500">
                      {user.city || (profile && profile.address && typeof profile.address === 'object' ? profile.address.city : '')}
                      {(user.city || (profile && profile.address && typeof profile.address === 'object' ? profile.address.city : '')) && 
                       (user.state || (profile && profile.address && typeof profile.address === 'object' ? profile.address.state : '')) ? ', ' : ''}
                      {user.state || (profile && profile.address && typeof profile.address === 'object' ? profile.address.state : '')}
                      {user.pincode || (profile && profile.address && typeof profile.address === 'object' ? profile.address.zipCode : '') ? 
                        ` - ${user.pincode || (profile && profile.address && typeof profile.address === 'object' ? profile.address.zipCode : '')}` : ''}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="p-6 border-t">
          <h3 className="text-lg font-medium text-gray-900 border-b pb-2 mb-4">Additional Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-yellow-500 mr-3 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Allergies</h4>
                  <p className="text-base font-medium text-gray-900">{profile?.allergies || 'None reported'}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Activity className="h-5 w-5 text-blue-500 mr-3 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Medical Conditions</h4>
                  <p className="text-base font-medium text-gray-900">{profile?.medicalConditions || 'None reported'}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <Calendar className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Last Health Check</h4>
                  <p className="text-base font-medium text-gray-900">{profile?.lastHealthCheck ? formatDate(profile.lastHealthCheck) : 'No record'}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Activity className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Weight</h4>
                  <p className="text-base font-medium text-gray-900">{profile?.weight ? `${profile.weight} kg` : 'Not recorded'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Donation History */}
        <div className="p-6 border-t">
          <h3 className="text-lg font-medium text-gray-900 border-b pb-2 mb-4">Donation History</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-500">Total Donations</h4>
              <p className="text-2xl font-bold text-red-600">{profile?.donationCount || 0}</p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-500">Last Donation</h4>
              <p className="text-base font-medium text-gray-900">
                {profile?.lastDonationDate ? formatDate(profile.lastDonationDate) : 'Never donated'}
              </p>
              <p className="text-xs text-gray-500">
                {profile?.lastDonationDate ? getTimeSinceLastDonation(profile.lastDonationDate) : ''}
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-500">Eligibility Status</h4>
              <p className={`text-base font-medium ${eligibility.color}`}>
                {eligibility.status}
              </p>
            </div>
          </div>
          
          {/* Donation Records */}
          {donationHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hospital</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {donationHistory.map((donation, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 text-gray-400 mr-2" />
                          {formatDate(donation.donationDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{donation.requestId || 'Direct Donation'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{donation.hospital || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          donation.status === 'completed' ? 'bg-green-100 text-green-800' : 
                          donation.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {donation.status ? donation.status.charAt(0).toUpperCase() + donation.status.slice(1) : 'Unknown'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <p className="text-gray-500">No donation records found</p>
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="p-6 border-t flex justify-end">
          <button
            onClick={handleEditDonor}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Donor
          </button>
        </div>
      </div>
    </div>
  );
};

export default DonorDetails;